import { hasValue } from '@repo/utils';

export type TTrajectoryCorrespondence = {
  id: number;
  citation?: { title?: string };
  parentTrajectoryId?: number;
  actualTrajId?: number;
};

function normalizeTitle(title: string | undefined): string {
  return (title ?? '').trim();
}

/**
 * Maps a published (or previously selected) trajectory id onto the matching item
 * in a draft-aware list.
 *
 * Prefer draft lineage via `actualTrajId`, then `parentTrajectoryId` (draft clone → published id), then a
 * unique same-name counterpart, then the newest same-name item.
 *
 * Note: WITSML also uses `parentTrajectoryId` for tie-in to a parent-wellbore trajectory.
 * That id is normally outside the current wellbore list, so it rarely collides with this
 * same-wellbore draft remap. Prefer title heuristics if the API later distinguishes lineage.
 */
export function resolveCorrespondingTrajectoryId(
  sourceId: number | null | undefined,
  trajectories: TTrajectoryCorrespondence[],
): number | null {
  if (!hasValue(sourceId) || trajectories.length === 0) {
    return null;
  }

  const byActualTrajId = trajectories.find((item) => item.actualTrajId === sourceId);

  if (byActualTrajId) {
    return byActualTrajId.id;
  }

  const byParent = trajectories.find((item) => item.parentTrajectoryId === sourceId);

  if (byParent) {
    return byParent.id;
  }

  const source = trajectories.find((item) => item.id === sourceId);

  if (!source) {
    return null;
  }

  const sourceTitle = normalizeTitle(source.citation?.title);

  if (!sourceTitle) {
    return sourceId;
  }

  const sameName = trajectories.filter((item) => normalizeTitle(item.citation?.title) === sourceTitle);

  if (sameName.length <= 1) {
    return sourceId;
  }

  return sameName.reduce((newest, item) => (item.id > newest.id ? item : newest)).id;
}
